import {
    BOLD_ITALIC_STAR,
    BOLD_ITALIC_UNDERSCORE,
    BOLD_STAR,
    BOLD_UNDERSCORE,
    ITALIC_STAR,
    ITALIC_UNDERSCORE,
    LINK,
    ORDERED_LIST,
    STRIKETHROUGH,
    UNORDERED_LIST,
} from '@lexical/markdown';
import { AutoLinkPlugin } from '@lexical/react/LexicalAutoLinkPlugin';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { EditorRefPlugin } from '@lexical/react/LexicalEditorRefPlugin';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { MarkdownShortcutPlugin } from '@lexical/react/LexicalMarkdownShortcutPlugin';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { TablePlugin } from '@lexical/react/LexicalTablePlugin';
import { RICH_TEXT_NODES, RICH_TEXT_THEME } from '@plato/shared-util/lexical-config';
import type { LexicalEditor, SerializedEditorState } from 'lexical';
import { useEffect, useRef, type ReactElement } from 'react';

const TRANSFORMERS = [
    UNORDERED_LIST,
    ORDERED_LIST,
    BOLD_ITALIC_STAR,
    BOLD_ITALIC_UNDERSCORE,
    BOLD_STAR,
    BOLD_UNDERSCORE,
    ITALIC_STAR,
    ITALIC_UNDERSCORE,
    STRIKETHROUGH,
    LINK,
];

const URL_MATCHER =
    /((https?:\/\/(www\.)?)|(www\.))[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)/;

const MATCHERS = [
    (text: string) => {
        const match = URL_MATCHER.exec(text);
        if (match === null) {
            return null;
        }
        const fullMatch = match[0];
        return {
            index: match.index,
            length: fullMatch.length,
            text: fullMatch,

            url: fullMatch.startsWith('http') ? fullMatch : `https://${fullMatch}`,
            attributes: { rel: 'noreferrer', target: '_blank' },
        };
    },
];

export type RichTextValue = SerializedEditorState;

type LexicalProps = {
    content: RichTextValue | null | undefined;
    contentEditable: ReactElement;
};

// Read-only Lexical wrapper used by RichTextViewer. For editable use, prefer the
// design-system component at @plato/ui/components/rich-text-input.
export const Lexical: React.FC<LexicalProps> = ({ content, contentEditable }) => {
    const editorRef = useRef<LexicalEditor>(null);

    const initialConfig = {
        namespace: 'RichTextViewer',
        nodes: RICH_TEXT_NODES,
        editable: false,
        editorState: content ? JSON.stringify(content) : undefined,
        theme: RICH_TEXT_THEME,
        onError: (error: Error) => {
            console.error('RichTextViewer error:', error);
        },
    };

    useEffect(() => {
        const editor = editorRef.current;

        if (!editor) {
            return;
        }

        editor.update(() => {
            try {
                editor.setEditorState(editor.parseEditorState(JSON.stringify(content)));
            } catch (error) {
                console.error('Error parsing editor state:', error);
            }
        });
    }, [content]);

    return (
        <LexicalComposer initialConfig={initialConfig}>
            <RichTextPlugin contentEditable={contentEditable} ErrorBoundary={LexicalErrorBoundary} />
            <ListPlugin />
            <LinkPlugin />
            <TablePlugin />
            <HistoryPlugin />
            <MarkdownShortcutPlugin transformers={TRANSFORMERS} />
            <AutoLinkPlugin matchers={MATCHERS} />
            <EditorRefPlugin editorRef={editorRef} />
        </LexicalComposer>
    );
};
